import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store/store';
import { queryParams } from '../../utils/query-string';
import { IListResponse } from '../../configs/constants';
import { IParams, IResources } from './resources';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 2 });

export const resourceApi = createApi({
  reducerPath: 'resourceApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: ['Resource'],
  endpoints: (builder) => ({
    getResources: builder.query<IListResponse<IResources>, { page: number; pageSize: number }>({
      query: ({ page, pageSize }) => {
        const params: IParams = {
          take: pageSize,
          skip: (page - 1) * pageSize,
        };
        return {
          url: `resources?${queryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: ['Resource'],
    }),
    addResource: builder.mutation<{ data: IResources; type: string }, any>({
      query: (body) => {
        return {
          url: 'resources',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Resource'],
    }),
    updateResource: builder.mutation<{ data: IResources; type: string }, any>({
      query: (body) => {
        const { id } = body;
        delete body.id;
        return {
          url: `resources/${id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['Resource'],
    }),
    deleteResource: builder.mutation<{ data: string; type: string }, any>({
      query: (id) => {
        return {
          url: `resources/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Resource'],
    }),
  }),
});

export const {
  useGetResourcesQuery,
  useAddResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,
} = resourceApi;
